<template>
  <Container class="bg-surface-default-low">
    <Breadcrumb
      :breads="breadcrumb"
      class="hidden md:flex"
      trackingZone="product - coming soon"
    />
    <BreadcrumbProductPage
      :breads="breadcrumb"
      class="px-24 md:hidden"
      trackingZone="product - coming soon"
    />
    <div class="flex flex-col pt-12 md:flex-row md:flex-nowrap md:pt-20">
      <Gallery
        v-if="images.length > 0"
        class="md:w-1/3"
        :product-images="images"
      />
      <TheMainContent class="px-24 md:pl-64 md:pr-0 md:w-2/3">
        <h1 class="punchline mt-24 mb-24 flex flex-col md:mt-0">
          <span>{{ data?.title }}</span>
          <span
            v-for="subtitle in data?.subtitle?.content"
            :key="subtitle.content.toString()"
            class="mt-24"
          >
            <span
              v-for="sub in subtitle.content"
              :key="sub.nodeType === 'text' ? sub.value : sub.nodeType"
            >
              <span v-if="sub.nodeType === 'text'">
                {{ sub.value }}
              </span>
            </span>
          </span>
        </h1>
        <div v-for="block in data?.blocks" :key="block.id">
          <div class="[&>div>a]:!w-fit [&>div]:justify-start">
            <Buttons v-if="block.type === 'Buttons'" v-bind="block.props" />
          </div>
          <RichText
            v-if="block.type === 'RichText'"
            class="mt-24"
            :rich-text="block.props.richText"
          />
        </div>
      </TheMainContent>
    </div>
    <RefinedProducts
      v-for="block in refinedProductsBlocks"
      :key="block.id"
      v-bind="block.props"
      class="mt-36"
    />
    <Faq v-for="block in faqBlocks" :key="block.id" v-bind="block.props" />
  </Container>
</template>

<script setup lang="ts">
import { createError, useRequestURL } from '#imports'
import { computed } from 'vue'

import Buttons from '@backmarket/nuxt-layer-cms/Buttons.vue'
import Faq from '@backmarket/nuxt-layer-cms/Faq.vue'
import RefinedProducts from '@backmarket/nuxt-layer-cms/RefinedProducts.vue'
import RichText from '@backmarket/nuxt-layer-cms/RichText.vue'
import { buildPropsWithCorrectLinks } from '@backmarket/nuxt-layer-cms/utils/buildPropsWithCorrectLinks'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'

import { useCMSHead } from '~/scopes/cms/composables/useCMSHead'
import { useFetchCMSPageContent } from '~/scopes/cms/composables/useFetchCMSPageContent'
import { filterBlocksByType } from '~/scopes/cms/helpers/filterBlocksByType'
import { findBlockByType } from '~/scopes/cms/helpers/findBlockByType'
import Breadcrumb from '~/scopes/navigation/components/Breadcrumb/Breadcrumb.vue'
import BreadcrumbProductPage from '~/scopes/product-core/components/presentationals/Breadcrumb/Breadcrumb.vue'
import Container from '~/scopes/product-core/components/presentationals/Container/Container.vue'

import Gallery from '../Iphone17EventPage/components/Gallery/Gallery.vue'

import translations from './ComingSoonPage.translations'

const currentUrl = useRequestURL()
const i18n = useI18n()
const locale = useI18nLocale()
const { data, error } = await useFetchCMSPageContent({
  fetchFallbackOnError: false,
  experienceLayerIsEnabled: false,
})
if (error.value) {
  throw createError({
    status: error.value.status,
    statusText: error.value.message,
    fatal: true,
  })
}

useCMSHead(data)

const refinedProductsBlocks = computed(() =>
  filterBlocksByType(data.value?.blocks, 'RefinedProducts'),
)

const faqBlocks = computed(() => filterBlocksByType(data.value?.blocks, 'Faq'))

const images = computed(() => {
  const carousel = findBlockByType(data.value?.blocks, 'ModularCardsCarousel')

  if (!carousel) return []

  return carousel.props.cards[0].sections[0].slots
    .filter((slot) => slot.type === 'Media')
    .map((slot) => ({
      src: slot.props.image?.src ?? '',
      title: slot.props.title ?? '',
    }))
})

const breadcrumb = computed(() => {
  const parentPageLink = data.value?.parentPageLink
    ? buildPropsWithCorrectLinks({
        props: data.value.parentPageLink,
        currentUrl,
      })
    : null

  return [
    {
      label: i18n(translations.home),
      url: `/${toBcp47Locale(locale.toLocaleLowerCase())}`,
    },
    {
      label: parentPageLink?.label ?? '',
      url: parentPageLink?.link.href ?? '',
    },
    { label: data.value?.titleBreadcrumb ?? '', url: '' },
  ]
})
</script>
